import { useContext, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Info, Warning } from '@mui/icons-material'
import { Box, ButtonBase, Grid, Typography, useTheme } from '@mui/material'

import { DashboardContext } from '../../providers/dashboard-provider'
import useDates from '../../utils/hooks/useDates'
import PlainTooltip from '../m3/plain-tooltip'

import useStyles from './index.styles'

interface IProps {
  alert: IAlertHistory
  variant: 'alerts-screen' | 'device-screen'
}

const AlertCard = (props: IProps) => {
  const { alert, variant } = props
  const { deviceId } = useParams<{ deviceId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedFolderId } = useContext(DashboardContext)
  const { formatDate, formatTime, timeFormat } = useDates()
  const [responseTooltipOpen, setResponseTooltipOpen] = useState(false)

  const openTooltip = () => setResponseTooltipOpen(true)
  const closeTooltip = () => setResponseTooltipOpen(false)

  const classes = useStyles
  const theme = useTheme()
  const leftBorderColor = alert.tags.includes('urgent')
    ? theme.palette.error.main
    : alert.tags.includes('warning')
      ? theme.palette.warning.light
      : theme.palette.ui.inversePrimary
  const cardBackgroundColor =
    deviceId === String(alert.nodeId) && variant === 'alerts-screen'
      ? theme.palette.secondary.light
      : alert.status === 'resolved'
        ? theme.palette.ui.inverseOnSurface
        : alert.tags.includes('urgent') && alert.responseHistory.length === 0
          ? theme.palette.error.light
          : theme.palette.background.paper
  const isActiveUrgentAlert = alert.status === 'active' && alert.tags.includes('urgent')
  const showNeedsResponseIcon = isActiveUrgentAlert && alert.responseHistory.length === 0
  const showRespondedIcon = isActiveUrgentAlert && alert.responseHistory.length > 0
  const formattedAlertTime = formatTime(alert.date, true)
  const formattedAlertPeriod = timeFormat !== '24hr' ? formatTime(alert.date).split(' ').pop() : ''
  const responseText = alert.responseHistory
    .map((response) => {
      const responder = `${response.firstName ?? ''} ${response.lastName ?? ''}`.trim() || 'Unknown user'
      const respondedAt = response.date ? `${formatDate(response.date)} ${formatTime(response.date)}` : ''
      return `Alert has been responded to by ${responder} at ${respondedAt}`
    })
    .join('\n')
  const tooltipText = showNeedsResponseIcon ? 'Alert needs a response' : showRespondedIcon ? responseText : ''

  return (
    <ButtonBase
      component={Box}
      sx={classes.container}
      disabled={variant === 'device-screen'}
      onClick={() =>
        variant === 'alerts-screen' &&
        selectedFolderId &&
        navigate(
          {
            pathname: `/dashboard/${selectedFolderId}/alerts/device/${alert.nodeId}`,
          },
          { state: location.state }
        )
      }
    >
      <Box sx={classes.leftBorder(leftBorderColor)} />
      <Grid container={true} sx={classes.card(cardBackgroundColor)}>
        <Grid container={true} sx={classes.timeContainer}>
          <Typography variant="subtitle1">{formattedAlertTime}</Typography>
          {!!formattedAlertPeriod && (
            <Typography variant="subtitle1" sx={classes.periodText}>
              {formattedAlertPeriod}
            </Typography>
          )}
        </Grid>
        <Box sx={classes.divider} />
        <Grid container={true} sx={classes.cardRightContainer}>
          <Grid container={true} sx={classes.detailsContainer}>
            <Typography variant="body1" sx={classes.alertName}>
              {alert.name}
            </Typography>
            <Typography variant="overline" sx={classes.locationText}>
              {alert.deviceLocation || 'Unknown Location'}
            </Typography>
          </Grid>
          {(showNeedsResponseIcon || showRespondedIcon) && (
            <PlainTooltip title={tooltipText} open={responseTooltipOpen} onClose={closeTooltip}>
              {showNeedsResponseIcon ? (
                <Warning sx={classes.needsResponseIcon} aria-label={tooltipText} onMouseEnter={openTooltip} onMouseLeave={closeTooltip} />
              ) : (
                <Info sx={classes.respondedIcon} aria-label={tooltipText} onMouseEnter={openTooltip} onMouseLeave={closeTooltip} />
              )}
            </PlainTooltip>
          )}
        </Grid>
      </Grid>
    </ButtonBase>
  )
}

export default AlertCard
